import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { TabItem, TabWrapper, TabTitle } from './style'

const SECTION3_SELECTOR = '.section3'

/**
 * Walk up the DOM tree to find the nearest ancestor that actually
 * scrolls (overflow auto/scroll). Falls back to window.
 */
const getScrollParent = (node) => {
  let parent = node?.parentElement

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent
    }

    parent = parent.parentElement
  }

  return window
}

const Tabs = ({ data, obj, isFirstTab, parentIndex }) => {
  const tabId = `tab-${parentIndex}`
  const wrapperRef = useRef(null)

  const [section3Height, setSection3Height] = useState(0)
  const [activeTabId, setActiveTabId] = useState( data?.length ? `${data[0].InsightsElementType}__0` : null )

  /**
   * Keep track of ".section3" height because it is sticky
   * at the top of the page.
   */
  useEffect(() => {
    const section3 = document.querySelector(SECTION3_SELECTOR)

    if (!section3) {
      return
    }

    const updateHeight = () => {
      setSection3Height(section3.offsetHeight)
    }

    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(section3)

    return () => resizeObserver.disconnect()
  }, [])

  /**
   * Observe each tab's corresponding section.
   *
   * Whichever section is currently closest to the sticky
   * tab/header area becomes the selected tab.
   */
  useEffect(() => {
    if (!data?.length) {
      return
    }

    const sections = data
      .map((tab, index) => {
        const id = `${tab.InsightsElementType}__${index}`
        return document.getElementById(id)
      })
      .filter(Boolean)

    if (!sections.length) {
      return
    }

    const scrollParent = getScrollParent(wrapperRef.current)

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSections = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top
          )

        if (visibleSections.length) {
          const activeSection = visibleSections[0]

          setActiveTabId(activeSection.target.id)
        }
      },
      {
        root: scrollParent === window ? null : scrollParent,

        // Account for the sticky section3 + tab bar.
        rootMargin: `-${section3Height + (wrapperRef.current?.offsetHeight || 0)}px 0px -50% 0px`,

        threshold: 0,
      }
    )

    sections.forEach((section) => observer.observe(section))

    return () => observer.disconnect()
  }, [data, section3Height])

  const tabClickedFunc = (e, id) => {
    e.preventDefault()

    const target = document.getElementById(id)

    if (!target) {
      return
    }

    // Immediately select the clicked tab.
    setActiveTabId(id)

    const scrollParent = getScrollParent(wrapperRef.current)

    // Combined height of the sticky headers sitting on top
    // of the content.
    const stickyOffset =
      section3Height + (wrapperRef.current?.offsetHeight || 0)

    if (scrollParent === window) {
      const targetTop =
        target.getBoundingClientRect().top + window.scrollY

      window.scrollTo({
        top: targetTop - stickyOffset,
        behavior: 'smooth',
      })
    } else {
      const containerRect = scrollParent.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()

      const targetOffsetWithinContainer =
        targetRect.top -
        containerRect.top +
        scrollParent.scrollTop

      scrollParent.scrollTo({
        top: targetOffsetWithinContainer - stickyOffset - 70,
        behavior: 'smooth',
      })
    }
  }

  return (
    <TabWrapper
      ref={wrapperRef}
      data-tabparent={tabId}
      id={tabId}
      style={{
        position: 'sticky',
        top: section3Height,
        display: `${data?.length === 0 ? 'none' : 'block'}`
      }}
      className={`tab-sticky active ${
        isFirstTab ? 'first-tab' : ''
      } tab-main-wrapper`}
    >
      {data.map((tab, index) => {
        const id = `${tab.InsightsElementType}__${index}`

        return (
          <TabItem
            key={index}
            onClick={(e) => tabClickedFunc(e, id)}
            href={`#${id}`}
            data-target={id}
            className={`nav-link ${
              activeTabId === id ? 'selected' : ''
            }`}
          >
            {tab.Title}
          </TabItem>
        )
      })}
    </TabWrapper>
  )
}

export default Tabs

Tabs.propTypes = {
  data: PropTypes.array,
  obj: PropTypes.object,
  isFirstTab: PropTypes.bool,
  parentIndex: PropTypes.number,
}
